import React, { useCallback, useEffect, useRef, useState } from "react";
import type { AppVersionMetadata } from "../types/appVersionMetadata";
import {
  analyzeZipArchive,
  type ZipAnalysis,
} from "../utils/zipAnalyzer";
import { logger } from '../log';
import styles from "./AddVersionDialog.module.css";

const log = logger('build-archive');

interface BuildArchiveSectionProps {
  dialogId: string;
  selectedFile: File | null;
  onFileChange: (file: File | null) => void;
  metadata: AppVersionMetadata;
  onMetadataChange: (patch: Partial<AppVersionMetadata>) => void;
  canAutofill: {
    gameEngine: boolean;
    operatingSystem: boolean;
    executablePath: boolean;
  };
  onAnalysisChange?: (analysis: ZipAnalysis | null) => void;
}

export function BuildArchiveSection({
  dialogId,
  selectedFile,
  onFileChange,
  metadata,
  onMetadataChange,
  canAutofill,
  onAnalysisChange
}: BuildArchiveSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<ZipAnalysis | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const canAutofillRef = useRef(canAutofill);
  const executableRef = useRef(metadata.executablePath);

  useEffect(() => {
    canAutofillRef.current = canAutofill;
  }, [canAutofill]);

  useEffect(() => {
    executableRef.current = metadata.executablePath;
  }, [metadata.executablePath]);

  useEffect(() => {
    onAnalysisChange?.(analysis);
  }, [analysis, onAnalysisChange]);

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (
      file.type !== "application/zip" &&
      !file.name.toLowerCase().endsWith(".zip")
    ) {
      setSelectionError("Only .zip archives are supported at the moment.");
      onFileChange(null);
      e.target.value = "";
      return;
    }
    setSelectionError(null);
    onFileChange(file);
    e.target.value = "";
  };

  const handleClearSelection = () => {
    onFileChange(null);
    setAnalysis(null);
    setSelectionError(null);
    setParseError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (!selectedFile) {
      setAnalysis(null);
      setParseError(null);
      setIsParsing(false);
      return;
    }

    setIsParsing(true);
    analyzeZipArchive(selectedFile)
      .then((result) => {
        if (cancelled) return;
        setAnalysis(result);
        setParseError(null);
        setIsParsing(false);
        applyAutofillFromHeuristics(
          result,
          onMetadataChange,
          canAutofillRef,
          executableRef.current
        );
      })
      .catch((err) => {
        if (cancelled) return;
        log.error('zip analysis failed', err);
        setAnalysis(null);
        setParseError("Unable to inspect this archive. Please try another file.");
        setIsParsing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedFile, onMetadataChange]);

  const detections = analysis?.heuristics;

  return (
    <section className={styles.zipInline} aria-labelledby={`${dialogId}-zip`}>
      <div className={styles.zipInlineHeader}>
        <label htmlFor={`${dialogId}-zip-input`} className={styles.label}>
          Build Archive *
        </label>
      </div>

      <div className={styles.zipInlineControls}>
        <span className={styles.zipButtonWrapper}>
          <button
            type="button"
            onClick={openFileDialog}
            className={`${styles.button} ${styles.buttonSecondary}`}
          >
            {selectedFile ? "Change Archive" : "Select Build Archive"}
          </button>
          {selectedFile && (
            <button
              type="button"
              onClick={handleClearSelection}
              className={styles.zipReset}
              aria-label="Clear selected archive"
            >
              ×
            </button>
          )}
        </span>
        <input
          id={`${dialogId}-zip-input`}
          ref={fileInputRef}
          type="file"
          accept=".zip,application/zip"
          hidden
          onChange={handleFileSelection}
        />
        {selectedFile ? (
          <span className={styles.zipStatus}>
            {isParsing
              ? "Reading archive…"
              : `Analyzed ${truncateName(selectedFile.name)}`}
          </span>
        ) : (
          <span className={styles.zipHelperText}>
            Select your packaged build (.zip).
          </span>
        )}
      </div>

      {selectionError && (
        <div className={styles.zipError}>{selectionError}</div>
      )}

      {!isParsing && parseError && (
        <div className={styles.zipError}>{parseError}</div>
      )}

      {!isParsing && selectedFile && detections && (
        <div className={styles.zipAnalysisRow}>
          {detections.suggestedOperatingSystem && (
            <span className={styles.zipBadgeInline}>
              OS · {formatLabel(detections.suggestedOperatingSystem)}
            </span>
          )}
          {detections.suggestedGameEngine && (
            <span className={styles.zipBadgeInline}>
              Engine · {formatLabel(detections.suggestedGameEngine)}
            </span>
          )}
          {detections.suggestedExecutable && (
            <span className={styles.zipBadgeInline}>
              Exec · {formatExecutableName(detections.suggestedExecutable)}
            </span>
          )}
          {detections.suggestedGameEngine === "unreal" && detections.hasPixelStreaming && (
            <span className={styles.zipBadgeInline}>
              PixelStreaming
            </span>
          )}
          {detections.suggestedGameEngine === "unreal" && !detections.hasPixelStreaming && (
            <span className={styles.zipHelperText}>
              No PixelStreaming plugin detected
            </span>
          )}
          {!detections.suggestedOperatingSystem &&
            !detections.suggestedGameEngine &&
            !detections.suggestedExecutable && (
              <span className={styles.zipHelperText}>
                No obvious signatures detected. You can still choose a file
                below.
              </span>
            )}
        </div>
      )}
    </section>
  );
}

function applyAutofillFromHeuristics(
  analysis: ZipAnalysis,
  onMetadataChange: (patch: Partial<AppVersionMetadata>) => void,
  canAutofillRef: React.MutableRefObject<{
    gameEngine: boolean;
    operatingSystem: boolean;
    executablePath: boolean;
  }>,
  currentExecutablePath: string
) {
  const suggestions: Partial<AppVersionMetadata> = {};
  const { heuristics } = analysis;
  const canAutofill = canAutofillRef.current;

  if (
    canAutofill.operatingSystem &&
    heuristics.suggestedOperatingSystem
  ) {
    suggestions.operatingSystem = heuristics.suggestedOperatingSystem;
  }

  if (canAutofill.gameEngine && heuristics.suggestedGameEngine) {
    suggestions.gameEngine = heuristics.suggestedGameEngine;
  }

  if (
    canAutofill.executablePath &&
    heuristics.suggestedExecutable &&
    !currentExecutablePath
  ) {
    suggestions.executablePath = heuristics.suggestedExecutable;
  }

  if (Object.keys(suggestions).length) {
    onMetadataChange(suggestions);
  }
}

function formatLabel(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatExecutableName(path: string) {
  const segments = path.split("/").filter(Boolean);
  return segments[segments.length - 1] || path;
}

function truncateName(name: string, max = 32) {
  const chars = Array.from(name);
  if (chars.length <= max) return name;
  return `${chars.slice(0, max - 3).join("")}...`;
}
